import { h, Component } from 'preact';
import Store from 'store/store';

import { accessNested, toQueryString, joinClasses, commaSeparatedList } from 'utils/utils';
import parseQueryString from 'utils/parseQueryString';
import { smoothScrollTo } from 'utils/smoothScroll';
import { loadVideoList } from 'utils/videos';

import VideoPlayer from 'components/bolt/videos/player';
import VideosPlaylist from 'components/bolt/videos/playlist/playlist';

import s from 'components/bolt/videos/videos.sss';

export default class Videos extends Component {

	constructor(props) {
		super(props);
		this.state = {
			videos: [],
			loaded: false,
		};

		this.changeActiveVideo = this.changeActiveVideo.bind(this);
		this.onEndedHandler = this.onEndedHandler.bind(this);
		this.onStartHandler = this.onStartHandler.bind(this);
		this.handleEscapeKey = this.handleEscapeKey.bind(this);

		this.preSelectedKey = accessNested(props, 'store.videos.preSelected');
	}

	componentDidMount() {
		// when videos page opens, make sure other videos (eg. from gallery) are paused
		Store.emit('PAUSE_ALL_VIDEOS');
		document.addEventListener('keydown', this.handleEscapeKey, false);
		this.loadVideos();
	}

	componentWillUnmount() {
		this.changeActiveVideo(); // clear active video
		document.removeEventListener('keydown', this.handleEscapeKey, false);
	}

	async loadVideos() {
		const store = this.props.store;
		const videos = await loadVideoList(store, store.videos?.overlayOpen);
		this.setState({ videos, loaded: true });
	}

	handleEscapeKey(e) {
		if (e.keyCode === 27) {
			const videoPath = accessNested(this.props, `store.appData.pages.${this.props.store?.videos?.overlayOpen}.path`);
			const videoIsRootDomain = videoPath === '/';
			if (!videoIsRootDomain) Store.emit('CLOSE_VIDEO_OVERLAY');
		}
	}

	findGalleryItemForQueryStringOrHash(videos, queryString = parseQueryString(), hash = location.hash.slice(1)) {
		let checkAgainst = queryString?.v || hash;

		return videos.find((item) => [item.vimeoId, item.youtubeId, item.key].includes(checkAgainst));
	}

	changeActiveVideo(video) {
		Store.emit('CHANGE_ACTIVE_VIDEO', video);
	}

	getNextVideo(activeVideo, videos) {
		if (!activeVideo || !videos?.length) {
			return;
		}

		const activeVidIndex = videos.findIndex((vid) => vid.key === activeVideo.key);
		return videos[activeVidIndex + 1];
	}

	scrollToQueriedVideo(queriedVideo) {
		window.setTimeout(() => {
			const activeVideoElement = accessNested(this.$videosPlaylist, `refs.${queriedVideo.key}.base`, null);
			const { isDesktopSize } = Store.get().client;
			const scroller = isDesktopSize ? this.$container : this.$videosPlaylist?.$scroller;

			if (activeVideoElement && scroller) {
				smoothScrollTo(scroller, activeVideoElement, 600, { offset: isDesktopSize ? 88 : 16 });
			}
		}, 0);
	}

	getLandingVideo() {
		// Because getting videos can take time, run only when data exists.
		if (!this.state.loaded) {
			return;
		}
		const videos = this.state.videos;
		const queryString = parseQueryString();
		const queriedVideo = this.findGalleryItemForQueryStringOrHash(videos, queryString);

		if (queriedVideo) {
			this.changeActiveVideo(queriedVideo);
			this.scrollToQueriedVideo(queriedVideo);
		}
		// We handled the video, remove it from the url
		// (I don't think it's bad to keep it in the url but it is the way it's been implemented)
		delete queryString.v;
		let newQS = toQueryString(queryString);
		if (newQS !== location.search) {
			history.replaceState?.({}, '', '.' + newQS);
		}

		let preSelected;
		if (this.preSelectedKey) {
			let lower = this.preSelectedKey.toLowerCase();
			let keysPriority = ['key', 'youtubeId', 'vimeoId'];
			let compare = (a, b, v) => a === v ? -1 : b === v ? 1 : 0;
			preSelected = videos.slice().sort((a, b) => {
				return keysPriority.reduce((o, k) => (
					o ||
				compare(a[k], b[k], this.preSelectedKey) ||
				compare(a[k] && a[k].toLowerCase(), b[k] && b[k].toLowerCase(), lower)
				), 0);
			}).shift();
		}

		return queriedVideo || preSelected || videos[0];
	}

	onEndedHandler() {
		const { store } = this.props;
		const activeVideo = accessNested(store, 'videos.activeVideo', this.state.videos[0]);
		const videoPath = accessNested(store, `appData.pages.${store?.videos?.overlayOpen}.path`);
		const videoIsRootDomain = videoPath === '/';
		const nextVideo = this.getNextVideo(activeVideo, this.state.videos);
		Store.emit('VIDEO_ENDED', activeVideo);
		if (nextVideo) {
			this.changeActiveVideo(nextVideo);
		} else if (!videoIsRootDomain) {
			Store.emit('CLOSE_VIDEO_OVERLAY');
		}
	}

	onStartHandler() {
		const activeVideo = accessNested(this.props, 'store.videos.activeVideo') || this.getLandingVideo();
		this.changeActiveVideo(activeVideo);
		Store.emit('VIDEO_STARTED', activeVideo, this.$player);
	}

	render(props, state) {
		const activeVideo = accessNested(props, 'store.videos.activeVideo') || this.getLandingVideo();
		let { pushDown, height: cookieHeight, cookieBannerOpen } = accessNested(props, 'store.cookies', {});
		const externalBannerOpen = accessNested(props, 'store.externalCookieBanner.bannerOpen');
		const sonyHeader = accessNested(props, 'store.appData.options.sonyFeatures.sonyHeader', false);
		const cookieMode = externalBannerOpen || (pushDown && cookieBannerOpen);
		let height = '';
		let transform = '';
		let paddingTop = (sonyHeader ? 34 : 0) + 'px';

		if (externalBannerOpen) {
			cookieHeight = accessNested(props, 'store.externalCookieBanner.height');
		}

		if (cookieMode) {
			height = `calc(100% - ${cookieHeight}px)`;
			transform = `translateY(${cookieHeight}px)`;
		}

		let hidePlaylist = state.videos.length < 2;

		const ageGateEnabled = props.store.appData?.options?.ageGate;
		let ageGateProtected = false;
		if (ageGateEnabled) {
			const activeVideosPage = props.store.videos?.overlayOpen;
			const ageGateOnVideoPage = (ageGateEnabled && !ageGateEnabled.onlyOnPages) || commaSeparatedList(ageGateEnabled.onlyOnPages).includes(activeVideosPage);
			const ageGateOpen = props.store?.ageGateOpen !== false;
			ageGateProtected = ageGateOnVideoPage && ageGateOpen;
		}

		return (
			<div
				ref={ref => this.$container = ref}
				class={joinClasses(s.container, props.store.videos?.overlayOpen)}
				style={{ ...props.style, height, transform, paddingTop }}
				inert={props.inert}
			>
				<VideoPlayer
					ref={e => this.$player = e}
					activeVideo={activeVideo}
					onEnded={this.onEndedHandler}
					onStart={this.onStartHandler}
					hiddenPlaylist={hidePlaylist}
					ageGateProtected={ageGateProtected}
				/>
				<VideosPlaylist
					ref={ref => this.$videosPlaylist = ref}
					videos={state.videos}
					activeVideo={activeVideo}
					// queriedVideo={this.state.queriedVideo}
					changeActiveVideo={this.changeActiveVideo}
					hidden={hidePlaylist}
				/>
			</div>
		);
	}
}
